import Parameters from "shared/parameters";
import API_SERVICE from "shared/services/api-service";

/** Outgoing payload (matches batch generate/report contract). */
export type ReportGenerateWsPayload = {
  startDate: string;
  endDate: string;
  reportType: string;
};

function trimBatchUrl(url: string): string {
  return String(url ?? "")
    .trim()
    .replace(/\s+/g, "");
}

function tokenFromRecord(r: Record<string, unknown>): string | undefined {
  const v = r.tokenHash;
  if (typeof v === "string" && v.trim()) return v.trim();
  return undefined;
}

function extractTokenHash(data: unknown): string | undefined {
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data == null || typeof data !== "object") return undefined;
  const o = data as Record<string, unknown>;

  const direct = tokenFromRecord(o);
  if (direct) return direct;

  if (typeof o.payload === "string" && o.payload.trim()) {
    return o.payload.trim();
  }

  /** Common APIs return `{ payload: { tokenHash } }` or `{ data: { payload: { tokenHash } } }`. */
  const tryPayload = (node: unknown): string | undefined => {
    if (node == null || typeof node !== "object") return undefined;
    const rec = node as Record<string, unknown>;
    const fromRec = tokenFromRecord(rec);
    if (fromRec) return fromRec;
    const inner = rec.payload;
    if (inner && typeof inner === "object") {
      return tokenFromRecord(inner as Record<string, unknown>);
    }
    return undefined;
  };

  const fromRootPayload = tryPayload(o.payload);
  if (fromRootPayload) return fromRootPayload;

  const d = o.data;
  if (d && typeof d === "object") {
    const fromData = tryPayload(d);
    if (fromData) return fromData;
  }

  return undefined;
}

/** WebSocket URL query: `token=<tokenhash>` from common `/auth/generate/token`. */
function getTokenQueryParams(token: string): string {
  const p = new URLSearchParams();
  if (token) p.set("token", token);
  return p.toString();
}

function withQuery(url: string, extraQuery: string): string {
  if (!extraQuery) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${extraQuery}`;
}

export function resolveReportGenerateWebSocketUrl(wsToken: string): string {
  const auth = getTokenQueryParams(wsToken);
  const batchHttp = trimBatchUrl(Parameters.ApiUrlBatch ?? "");
  try {
    const normalized = batchHttp.endsWith("/") ? batchHttp : `${batchHttp}/`;
    const u = new URL(normalized);
    const host = u.host;
    if (!host) throw new Error("missing host");
    const wsScheme = u.protocol === "https:" ? "wss:" : "ws:";
    const wsBase = `${wsScheme}//${host}/ws/api/batch/generate/report`;
    return withQuery(wsBase, auth);
  } catch {
    return withQuery(``, auth);
  }
}

/** Path (+ query flag only) for error messages — no secrets. */
export function reportWsUrlHint(wsUrl: string): string {
  try {
    const u = new URL(wsUrl);
    const q = u.search ? " (includes query params)" : "";
    return `${u.pathname}${q}`;
  } catch {
    return "(invalid URL)";
  }
}

function closeCodeMessage(
  code: number,
  reason: string,
  urlHint?: string
): string {
  const r = reason?.trim();
  if (r) return r;
  const tried = urlHint ? ` Attempted: ${urlHint}.` : "";
  switch (code) {
    case 1000:
    case 1005:
      return "";
    case 1006:
      return `Connection closed before handshake completed.${tried} Ensure common auth/token API succeeds and the batch WebSocket accepts the token query param.`;
    case 1002:
      return "Protocol error — server rejected the WebSocket request.";
    case 1008:
      return "Policy violation — server rejected the connection.";
    case 1011:
      return "Server error while establishing WebSocket.";
    default:
      return `WebSocket closed with code ${code}.`;
  }
}

/**
 * First message: `{ startDate, endDate, reportType, token }`.
 * Real WebSocket HTTP headers are not available in the browser.
 */
function serializeReportWsMessage(
  payload: ReportGenerateWsPayload,
  wsToken: string
): string {
  return JSON.stringify({
    ...payload,
    token: wsToken,
  });
}

export type RequestReportWsOptions = {
  /**
   * Max wait for token fetch + WebSocket open + first send (defaults to 45s).
   * After the request is sent, the client stays open until the server closes the connection.
   */
  timeoutMs?: number;
  /**
   * Called for every inbound WebSocket frame (raw `MessageEvent.data` as string).
   * Hook e.g. `getReportRequests` to refresh the recent-requests table on progress updates.
   */
  onMessageReceived?: (rawData: string) => void | Promise<void>;
};

/**
 * Calls common `auth/generate/token`, then opens batch WebSocket with `token` (query + body).
 * Keeps the socket open for server messages; resolves when the server closes normally (1000/1005),
 * rejects on server error payloads or abnormal close.
 */
export async function requestReportViaWebSocket(
  payload: ReportGenerateWsPayload,
  options?: RequestReportWsOptions
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? 45000;

  const { data } = await API_SERVICE.generateReportWebSocketAuthToken();
  const wsToken = extractTokenHash(data);
  if (!wsToken) {
    throw new Error(
      "Could not obtain WebSocket token (tokenhash missing in auth response)."
    );
  }

  const wsUrl = resolveReportGenerateWebSocketUrl(wsToken);
  const messageHook = options?.onMessageReceived;

  const notifyMessage = (raw: string) => {
    if (!messageHook) return;
    void Promise.resolve()
      .then(() => messageHook(raw))
      .catch(() => {});
  };

  return new Promise((resolve, reject) => {
    let done = false;
    let opened = false;
    let ws: WebSocket;

    const finish = (
      action: "resolve" | "reject",
      err?: Error,
      opts?: { skipClientClose?: boolean }
    ) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (!opts?.skipClientClose) {
        try {
          ws?.close();
        } catch {
          /* ignore */
        }
      }
      if (action === "resolve") resolve();
      else reject(err ?? new Error("Report request failed"));
    };

    const timer = setTimeout(() => {
      finish("reject", new Error("Report request timed out"));
    }, timeoutMs);

    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      clearTimeout(timer);
      reject(e instanceof Error ? e : new Error(String(e)));
      return;
    }

    ws.onopen = () => {
      opened = true;
      try {
        ws.send(serializeReportWsMessage(payload, wsToken));
        clearTimeout(timer);
      } catch (e) {
        finish(
          "reject",
          e instanceof Error ? e : new Error("Failed to send report request")
        );
      }
    };

    ws.onmessage = (ev) => {
      const raw =
        typeof ev.data === "string" ? ev.data : String(ev.data ?? "");
      notifyMessage(raw);

      let msg: unknown;
      try {
        msg = JSON.parse(raw);
        console.log("msg onmessage", msg);
      } catch {
        return;
      }
      if (msg && typeof msg === "object") {
        const o = msg as Record<string, unknown>;
        const errMsg =
          o.errorMessage ??
          o.message ??
          o.error ??
          (o.payload as Record<string, unknown> | undefined)?.errorMessage;
        const status = String(o.status ?? "").toUpperCase();
        if (
          o.success === false ||
          status === "ERROR" ||
          status === "FAILED" ||
          o.error
        ) {
          finish(
            "reject",
            new Error(
              typeof errMsg === "string" && errMsg.length > 0
                ? errMsg
                : "Report request failed"
            )
          );
        }
      }
    };

    /** Do not reject here — wait for `onclose` so we get code/reason (e.g. 1006). */
    ws.onerror = () => {
      /* no-op: details unavailable in browser */
    };

    ws.onclose = (ev) => {
      if (done) return;

      if (ev.code === 1000 || ev.code === 1005) {
        finish("resolve", undefined, { skipClientClose: true });
        return;
      }

      if (!opened) {
        const hint = reportWsUrlHint(wsUrl);
        const detail = closeCodeMessage(ev.code, ev.reason || "", hint);
        finish(
          "reject",
          new Error(
            detail ||
              "Could not connect to the report WebSocket. Set REACT_APP_BATCH_REPORT_WS_URL or REACT_APP_BATCH_REPORT_WS_PATH to match your server."
          ),
          { skipClientClose: true }
        );
        return;
      }

      finish(
        "reject",
        new Error(
          closeCodeMessage(ev.code, ev.reason || "", reportWsUrlHint(wsUrl)) ||
            `Connection closed (${ev.code})`
        ),
        { skipClientClose: true }
      );
    };
  });
}
